import React, { useState, useEffect } from 'react';
import { Box, Typography, Rating, TextField, Button, Avatar, Divider, CircularProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';

const ADJECTIVES = ["Amazing", "Hot", "Incredible", "Gorgeous", "Perfect", "Stunning", "Wild", "Sweet"];
const rng = (seed) => { let x = seed; return () => { x = (x * 1664525 + 1013904223) & 0xffffffff; return Math.abs(x) / 0xffffffff; }; };

// Generate plausible-looking anon username
const anonName = (idx) => {
  const rand = rng(idx * 7919);
  const adj = ADJECTIVES[Math.floor(rand() * ADJECTIVES.length)];
  const num = Math.floor(rand() * 9000) + 1000;
  return `${adj}Fan${num}`;
};

const timeAgo = (ts) => {
  const secs = Math.floor((Date.now() - new Date(ts + 'Z').getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h ago`;
  return `${Math.floor(secs/86400)}d ago`;
};

const PerformerReviews = ({ performerName }) => {
  const [reviews, setReviews]       = useState([]);
  const [avg, setAvg]               = useState(0);
  const [count, setCount]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [myRating, setMyRating]     = useState(0);
  const [comment, setComment]       = useState('');
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  const sessionId = (() => {
    try {
      let s = localStorage.getItem('chat_session');
      if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('chat_session', s); }
      return s;
    } catch { return 'anon'; }
  })();

  useEffect(() => {
    if (!performerName) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/reviews/${encodeURIComponent(performerName)}`)
      .then(r => {
        setAvg(r.data.avg || 0);
        setCount(r.data.count || 0);
        setReviews(r.data.reviews || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [performerName]);

  const handleSubmit = async () => {
    if (!myRating) return;
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/reviews`, {
        performer_name: performerName,
        session_id: sessionId,
        rating: myRating,
        comment: comment.trim(),
        lang: new URLSearchParams(window.location.search).get('lg') || 'en',
      });
      setSubmitted(true);
      setCount(c => c + 1);
      setAvg(prev => parseFloat(((prev * (count) + myRating) / (count + 1)).toFixed(1)));
      if (comment.trim()) {
        setReviews(prev => [{ rating: myRating, comment: comment.trim(), created_at: new Date().toISOString().replace('T',' ').slice(0,19) }, ...prev]);
      }
    } catch (e) {
      if (e.response?.status === 409) setAlreadyReviewed(true);
      else setSubmitted(true);
    }
    setSubmitting(false);
  };

  if (loading) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />

      {/* Header + avg */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Member Reviews</Typography>
        {count > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rating value={avg} precision={0.5} readOnly size="small" sx={{ color: '#FF4196' }} />
            <Typography sx={{ fontWeight: 700, color: '#FF4196' }}>{avg}</Typography>
            <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>({count} review{count !== 1 ? 's' : ''})</Typography>
          </Box>
        )}
      </Box>

      {/* Review list */}
      {reviews.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {reviews.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#FF4196', fontSize: '0.8rem', flexShrink: 0 }}>
                {anonName(i).charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{anonName(i + count * 3)}</Typography>
                  <Rating value={r.rating} readOnly size="small" sx={{ color: '#FF4196' }} />
                  <Typography sx={{ color: '#bbb', fontSize: '0.75rem' }}>{timeAgo(r.created_at)}</Typography>
                </Box>
                {r.comment && (
                  <Typography sx={{ fontSize: '0.85rem', color: '#444', mt: 0.3, lineHeight: 1.5 }}>
                    {r.comment}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ color: '#999', fontSize: '0.85rem', mb: 2 }}>No reviews yet — be the first!</Typography>
      )}

      {/* Submit form */}
      <Box sx={{ bgcolor: '#f9f9f9', borderRadius: 2, p: 2 }}>
        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <StarIcon sx={{ color: '#FF4196', fontSize: 32 }} />
            <Typography sx={{ fontWeight: 700, color: '#FF4196' }}>Thanks for your review!</Typography>
          </Box>
        ) : alreadyReviewed ? (
          <Typography sx={{ color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>
            You've already reviewed {performerName}.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
              Rate your experience with {performerName}
            </Typography>
            <Rating
              value={myRating}
              onChange={(_, v) => setMyRating(v)}
              size="large"
              sx={{ color: '#FF4196', mb: 1.5 }}
            />
            <TextField
              placeholder="Share your experience (optional)..."
              multiline rows={2}
              value={comment}
              onChange={e => setComment(e.target.value.slice(0, 300))}
              fullWidth
              size="small"
              sx={{ mb: 1.5, bgcolor: '#fff', borderRadius: 1,
                '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#FF4196' },
                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#FF4196' } }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!myRating || submitting}
              sx={{ background: 'linear-gradient(45deg,#e91e63,#9c27b0)', borderRadius: '50px',
                textTransform: 'none', fontWeight: 700, px: 3 }}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Review'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PerformerReviews;
